import {
    basisHint,
    formatCount,
    formatExclusiveRange,
    formatGuayaquilStamp,
    formatMoney,
    formatPercent,
} from '../../../utils/salesReportUtils';

const directionClass = (percent) => {
    if (percent === null || percent === undefined) return 'is-up';
    if (percent > 0) return 'is-up';
    if (percent < 0) return 'is-down';
    return 'is-flat';
};

const Delta = ({ change, money = false }) => {
    if (!change) return null;
    const amount = money ? formatMoney(change.amount) : formatCount(change.amount);
    const signedAmount = change.amount > 0 && money ? `+${amount}` : amount;

    return (
        <p className={`sr-delta ${directionClass(change.percent)}`}>
            <span>{formatPercent(change.percent)}</span>
            <small>
                {signedAmount} vs periodo anterior
            </small>
        </p>
    );
};

const Metric = ({ label, value, change, money = false, lead = false }) => (
    <article className={`sr-metric${lead ? ' sr-metric--lead' : ''}`}>
        <p className="sr-kicker">{label}</p>
        <p className="sr-value">{value}</p>
        <Delta change={change} money={money} />
    </article>
);

const Stat = ({ label, value }) => (
    <article className="sr-stat">
        <p className="sr-kicker">{label}</p>
        <strong>{value}</strong>
    </article>
);

export const SalesReportSkeleton = () => (
    <div className="sr-skeleton-grid" aria-hidden="true">
        <div className="sr-skeleton-card" />
        <div className="sr-skeleton-card" />
        <div className="sr-skeleton-card" />
        <div className="sr-skeleton-card" />
        <div className="sr-skeleton-chart" />
    </div>
);

const SalesReportSummary = ({ summary, comparison, basis, hasProductFilter, generatedAt }) => {
    if (!summary) return null;

    const change = comparison?.change;
    const comparedWith = comparison?.range
        ? formatExclusiveRange(comparison.range.start, comparison.range.endExclusive)
        : '';

    return (
        <section aria-label="Totales del reporte">
            <div className="sr-metrics">
                <Metric lead label="Ventas netas" value={formatMoney(summary.netSales)} change={change?.netSales} money />
                <Metric label="Ventas brutas" value={formatMoney(summary.grossSales)} change={change?.grossSales} money />
                <Metric label="Pedidos" value={formatCount(summary.orderCount)} change={change?.orderCount} />
                <Metric
                    label="Ticket promedio"
                    value={formatMoney(summary.averageOrderValue)}
                    change={change?.averageOrderValue}
                    money
                />
            </div>
            <div className="sr-secondary">
                <Stat label="Unidades" value={formatCount(summary.unitsSold)} />
                <Stat label="Devoluciones" value={formatMoney(summary.returnTotal)} />
                <Stat label="Descuentos" value={formatMoney(summary.discountTotal)} />
                <Stat label="IVA" value={formatMoney(summary.taxTotal)} />
                <Stat label="Envío" value={formatMoney(summary.shippingTotal)} />
                <Stat label="Clientes" value={formatCount(summary.uniqueCustomers)} />
            </div>
            <div className="sr-notes">
                <p className="sr-note mb-0">
                    La venta neta es la venta bruta menos devoluciones aprobadas o completadas. Un pedido reembolsado sin monto de devolución cuenta como devolución total.
                </p>
                <p className="sr-note mb-0">{basisHint(basis)}</p>
                {hasProductFilter && (
                    <p className="sr-note mb-0">
                        El filtro de producto limita el reporte a pedidos que lo contienen. Esos totales siguen incluyendo las otras líneas. La pestaña Productos muestra solo las líneas coincidentes.
                    </p>
                )}
                {comparedWith && <p className="sr-note mb-0">Comparado con {comparedWith}.</p>}
                {generatedAt && (
                    <p className="sr-note mb-0">Generado {formatGuayaquilStamp(generatedAt, true)}.</p>
                )}
            </div>
        </section>
    );
};

export default SalesReportSummary;
